import { useState } from "react";
import { Plus, Trash2, Eye, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useParserTrainingExamples,
  useUpdateTrainingExample,
  useDeleteTrainingExample,
  ParserTrainingExample,
} from "@/hooks/useParserTraining";
import { AddTrainingExampleDialog } from "./AddTrainingExampleDialog";
import { safeFormat } from "@/lib/formatDateTime";

export function ParserTrainingPanel() {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [viewExample, setViewExample] = useState<ParserTrainingExample | null>(null);
  const { data: examples, isLoading } = useParserTrainingExamples();
  const updateExample = useUpdateTrainingExample();
  const deleteExample = useDeleteTrainingExample();

  const handleToggleActive = (id: string, isActive: boolean) => {
    updateExample.mutate({ id, is_active: !isActive });
  };

  const handleDelete = (id: string) => {
    if (confirm("Delete this training example?")) {
      deleteExample.mutate(id);
    }
  };

  const getBookingTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      flight: "bg-blue-600",
      hotel: "bg-green-600",
      car: "bg-orange-600",
      activity: "bg-purple-600",
    };
    return (
      <Badge className={colors[type] || "bg-gray-600"}>
        {type}
      </Badge>
    );
  };

  return (
    <div className="mt-10 rounded-xl border border-border bg-card shadow-soft overflow-hidden">
      <div className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="h-5 w-5 text-accent" />
          <div>
            <h2 className="font-display text-lg font-semibold text-card-foreground">
              Parser Training Examples
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Add email examples to improve AI parsing accuracy
            </p>
          </div>
        </div>
        <Button onClick={() => setShowAddDialog(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Example
        </Button>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-muted-foreground">Loading...</div>
      ) : examples?.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">
          <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No training examples yet.</p>
          <p className="text-sm mt-1">
            Add examples to help the AI better parse confirmation emails.
          </p>
        </div>
      ) : (
        <div className="max-h-[420px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Extracted Items</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {examples?.map((example) => {
                const extractedData = example.extracted_data || {};
                const itemCount =
                  (extractedData.flights?.length || 0) +
                  (extractedData.hotels?.length || 0) +
                  (extractedData.cars?.length || 0) +
                  (extractedData.activities?.length || 0);

                return (
                  <TableRow key={example.id}>
                    <TableCell>
                      {getBookingTypeBadge(example.booking_type)}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm">
                      {example.email_subject || "(no subject)"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {itemCount} item{itemCount !== 1 ? "s" : ""}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {safeFormat(example.created_at, "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={example.is_active}
                        onCheckedChange={() =>
                          handleToggleActive(example.id, example.is_active)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setViewExample(example)}
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(example.id)}
                          className="text-destructive hover:text-destructive"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <AddTrainingExampleDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
      />

      {/* View Example Dialog */}
      <Dialog open={!!viewExample} onOpenChange={() => setViewExample(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Training Example Details</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            {viewExample && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">Type</h4>
                  {getBookingTypeBadge(viewExample.booking_type)}
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Subject</h4>
                  <p className="text-sm text-muted-foreground">{viewExample.email_subject || "(no subject)"}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Email Content (first 500 chars)</h4>
                  <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">
                    {viewExample.email_content.slice(0, 500)}{viewExample.email_content.length > 500 ? "..." : ""}
                  </pre>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Extracted Data</h4>
                  <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto">
                    {JSON.stringify(viewExample.extracted_data, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
